import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { apiFetch } from '../services/api';
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import SEO from '../components/SEO';
import { calculateDeliveryFeeCents, deliveryZone } from '../utils/delivery';
import { useAuth } from '../context/AuthContext';
import { getPricing, fmtPrice } from '../utils/pricing';
import { submitPayment } from '../utils/checkoutSubmit';
import './CheckoutPage.css';
import { STRIPE_PUBLISHABLE_KEY as STRIPE_PK } from '../config/env';
import { isPrerendering } from '../prerenderData';
import { cardImage } from '../utils/productImages';
import { parseAddons, addonLines, addonsPayload, encodeAddons } from '../utils/addons';

const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const STATES = ['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

function variantLabelFor(width, projection) {
  const w = Number(width), p = Number(projection);
  return Number.isFinite(w) && Number.isFinite(p) ? `${w} × ${p} m` : '';
}

// Validates a form snapshot. Returns null when ready to submit, or a string
// describing what's missing/wrong. Owned by the page so both the submit
// handler and the CTA's disabled state can call it.
function validateForm(c) {
  if (!c.full_name.trim()) return 'Please enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email.trim())) return 'Please enter a valid email.';
  if (!c.address_line1.trim()) return 'Please enter your street address.';
  if (!c.suburb.trim()) return 'Please enter your suburb.';
  if (!/^\d{4}$/.test(c.postcode.trim())) return 'Please enter a valid 4-digit postcode.';
  if (deliveryZone(c.postcode.trim()) === 3) return 'Delivery to your postcode is quoted separately. Please request a freight quote below - we cannot take payment until the freight cost is confirmed.';
  return null;
}

// Order summary panel — used in both the desktop sidebar and the mobile
// collapsible. The Was/Now/Savings block only renders when there's an
// actual discount; otherwise we show a single Subtotal line.
function OrderSummary({ product, variantLabel, colourName, pricing, addons, postcodeStr, discount }) {
  const validPostcode = /^\d{4}$/.test(postcodeStr.trim());
  const zone = validPostcode ? deliveryZone(postcodeStr) : null;
  const deliveryCents = validPostcode ? calculateDeliveryFeeCents(postcodeStr) : null;
  const isZone3 = zone === 3;
  const addonCents = Math.round(addons.total * 100);
  const subtotalCents = pricing ? Math.round(pricing.sale * 100) + addonCents : 0;
  const savingsCents = pricing && pricing.discountPct > 0
    ? Math.round((pricing.original - pricing.sale) * 100)
    : 0;
  const discountCents = discount ? discount.discount_cents : 0;
  const totalCents = Math.max(0, subtotalCents + (deliveryCents || 0) - discountCents);

  return (
    <div className="co-summary">
      {cardImage(product) && (
        <div className="co-summary-img"><img src={cardImage(product)} alt={product.name} /></div>
      )}
      <div className="co-summary-product">
        <div className="co-summary-name">{product.name}</div>
        {variantLabel && <div className="co-summary-meta">Size: {variantLabel}</div>}
        {colourName && <div className="co-summary-meta">Colour: {colourName}</div>}
      </div>

      {addons.lines.length > 0 && (
        <ul className="co-summary-addons">
          {addons.lines.map((l) => (
            <li key={l.slug} className="co-summary-row co-summary-row--muted">
              <span>{l.quantity > 1 ? `${l.quantity} × ` : ''}{l.name}</span>
              <span>{l.included ? 'Included free' : `$${fmtPrice(l.lineTotal)}`}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="co-summary-divider" />

      {pricing && pricing.discountPct > 0 ? (
        <>
          <div className="co-summary-row co-summary-row--muted">
            <span>Was</span>
            <span className="co-summary-strike">${fmtPrice(pricing.original)}</span>
          </div>
          <div className="co-summary-row co-summary-row--now">
            <span>Now</span>
            <span className="co-summary-now">${fmtPrice(pricing.sale)}</span>
          </div>
          <div className="co-savings">
            <svg className="co-savings-tick" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 12l5 5 9-11" /></svg>
            <span>You save ${fmtPrice(savingsCents / 100)} · {pricing.discountPct}% off</span>
          </div>
        </>
      ) : (
        <div className="co-summary-row">
          <span>Subtotal</span>
          <span>{pricing ? `$${fmtPrice(subtotalCents / 100)}` : '-'}</span>
        </div>
      )}

      <div className="co-summary-divider" />

      <div className="co-summary-row co-summary-row--muted">
        <span>Delivery</span>
        <span>
          {!validPostcode
            ? 'Enter postcode'
            : zone === 1
              ? 'Free (most of Melbourne)'
              : zone === 2
                ? `$${(deliveryCents / 100).toLocaleString()}`
                : 'Quoted separately'}
        </span>
      </div>
      {discountCents > 0 && (
        <div className="co-summary-row co-summary-row--discount">
          <span>{discount.label || discount.code}</span>
          <span>-${fmtPrice(discountCents / 100)}</span>
        </div>
      )}
      <div className="co-summary-row co-summary-row--total">
        <span>Total <span className="co-summary-gst">incl. GST</span></span>
        <span>{!pricing ? '-' : isZone3 ? 'Quoted on request' : `$${fmtPrice(totalCents / 100)}`}</span>
      </div>
      <p className="co-summary-gst-note">All prices include GST.</p>
      <p className="co-summary-secure">
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
        Secure checkout · Powered by Stripe
      </p>
    </div>
  );
}

// The form lives inside <Elements> so it can call useStripe()/useElements().
// All state is owned by the parent <CheckoutPage> and passed in as props,
// so the parent can render the same field values into the mobile summary.
function CheckoutForm({
  product,
  pricing,
  variantLabel,
  colourName,
  customer,
  setCustomer,
  notes,
  setNotes,
  productSlug,
  width,
  projection,
  colourCode,
  addons,
  addonQty,
  user,
  totalCents,
  navigate,
  discount,
  setDiscount,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [error, setError] = useState('');
  // Stripe fires onReady on the ExpressCheckoutElement once it knows which
  // wallets (if any) the user's browser supports. If none, we hide the
  // whole row + divider rather than show an empty band.
  // 'loading' reserves space (no layout shift while wallet detection runs),
  // 'ready' shows the buttons + divider, 'unavailable' collapses the row.
  const [expressStatus, setExpressStatus] = useState('loading');

  // Keep the Stripe Elements amount in sync with the running total — the
  // <Elements> options object is only read on mount, so updates have to
  // happen imperatively. This drives the Apple/Google Pay button price.
  useEffect(() => {
    if (elements && totalCents > 0) {
      elements.update({ amount: totalCents });
    }
  }, [elements, totalCents]);

  // Fallback: collapse the wallet row if Stripe never signals readiness (e.g.
  // a browser with no wallet), so the reserved skeleton doesn't linger.
  useEffect(() => {
    const t = setTimeout(() => setExpressStatus((s) => (s === 'loading' ? 'unavailable' : s)), 25000);
    return () => clearTimeout(t);
  }, []);

  // Shared payment flow used by both the regular form submit and the
  // ExpressCheckoutElement (Apple Pay / Google Pay) onConfirm callback.
  // customerOverride lets the wallet path skip the form entirely — when
  // the buyer pays via Apple/Google Pay, the wallet sheet collects name,
  // email, phone, and shipping address itself, and we forward those to
  // the server instead of relying on the form fields.
  const placeOrder = async (customerOverride) => {
    const c = customerOverride || customer;
    const v = validateForm(c);
    if (v) { setError(v); return false; }
    if (!stripe || !elements) return false;
    setError('');
    setSubmitting(true);
    try {
      // SEC-06 Phase 5: customer_session cookie attaches the authenticated
      // user automatically via apiFetch's credentials:'include'. The server
      // endpoint uses customerAuthOptional so guest checkout works too.
      // submitPayment is the single shared payment path (also used by the
      // product-page express wallets).
      await submitPayment({
        stripe,
        elements,
        payload: {
          product_slug: productSlug,
          width: width || null,
          projection: projection || null,
          colour: colourName ? { name: colourName, code: colourCode } : null,
          addons: addonsPayload(addonQty),
          customer: c,
          notes,
          discount_code: discount ? discount.code : '',
          referrer: typeof document !== 'undefined' ? document.referrer : '',
        },
        returnUrl: `${window.location.origin}/checkout/success`,
        receiptEmail: c.email,
      });
      navigate('/checkout/success');
      return true;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Ask the server what the code is worth. The figure shown here is only a
  // preview: /create-intent prices the code again from the database when it
  // builds the charge, so nothing the browser holds can move what is charged.
  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setCodeBusy(true);
    setCodeError('');
    try {
      const res = await apiFetch('/api/checkout/validate-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          product_slug: productSlug,
          width: width || null,
          projection: projection || null,
          addons: addonsPayload(addonQty),
          postcode: customer.postcode,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'That code could not be applied.');
      setDiscount({ code: data.code, label: data.label, discount_cents: data.discount_cents });
    } catch (err) {
      setCodeError(err.message);
      setDiscount(null);
    } finally {
      setCodeBusy(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); placeOrder(); };

  // Apple Pay / Google Pay open the wallet sheet, which collects email,
  // phone, name, and shipping address itself. onConfirm fires AFTER the
  // user authorises in the wallet — at that point we have everything we
  // need and can call placeOrder() with a synthesised customer object.
  const handleExpressClick = (event) => {
    const pc = customer.postcode.trim();
    const fee = /^\d{4}$/.test(pc) ? calculateDeliveryFeeCents(pc) : null;
    // Zone 3 (fee null): can't quote freight here. Show a placeholder rate now
    // and reject at the shipping-address step so no unquoted freight is charged.
    const rateAmount = fee == null ? 15000 : fee;
    event.resolve({
      emailRequired: true,
      phoneNumberRequired: true,
      shippingAddressRequired: true,
      allowedShippingCountries: ['AU'],
      shippingRates: [{
        id: fee === 0 ? 'free' : 'standard',
        amount: rateAmount,
        displayName: fee === 0 ? 'Free delivery' : 'Delivery',
      }],
      lineItems: pricing ? [
        { name: `${product.name}${variantLabel ? ' - ' + variantLabel : ''}`, amount: Math.round(pricing.sale * 100) },
        ...addons.lines.filter((l) => !l.included).map((l) => ({
          name: `${l.quantity > 1 ? `${l.quantity} x ` : ''}${l.name}`,
          amount: Math.round(l.lineTotal * 100),
        })),
      ] : undefined,
    });
  };

  const handleExpressShippingChange = (event) => {
    const pc = event.address && event.address.postal_code;
    if (!pc || !/^\d{4}$/.test(pc)) { event.reject(); return; }
    const fee = calculateDeliveryFeeCents(pc);
    // Zone 3: freight quoted separately - cannot be charged via express checkout.
    if (fee == null) { event.reject(); return; }
    event.resolve({
      shippingRates: [{
        id: fee === 0 ? 'free' : 'standard',
        amount: fee,
        displayName: fee === 0 ? 'Free delivery' : 'Delivery',
      }],
    });
  };

  const handleExpressConfirm = async (event) => {
    const sa = (event.shippingAddress && event.shippingAddress.address) || {};
    const name = (event.shippingAddress && event.shippingAddress.name) || (event.billingDetails && event.billingDetails.name) || '';
    const c = {
      full_name: name,
      email: event.email || (event.billingDetails && event.billingDetails.email) || '',
      phone: event.phone || (event.billingDetails && event.billingDetails.phone) || '',
      address_line1: sa.line1 || '',
      address_line2: sa.line2 || '',
      suburb: sa.city || '',
      state: sa.state || 'VIC',
      postcode: sa.postal_code || '',
    };
    setCustomer(c);
    await placeOrder(c);
  };

  const update = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });
  const placeOrderLabel = submitting
    ? 'Processing…'
    : `Place Order - $${fmtPrice(totalCents / 100)}`;
  const formValid = !validateForm(customer);

  return (
    <form id="co-form" className="co-form" onSubmit={handleSubmit}>
      {user && (
        <p className="co-signedin-banner">
          Signed in as <strong>{user.email}</strong>. <Link to="/account">Account →</Link>
        </p>
      )}
      {!user && (
        <p className="co-signin-link">
          Have an account? <Link to={`/sign-in?next=${encodeURIComponent('/checkout?' + new URLSearchParams({ product: productSlug, w: width, p: projection, colour: colourName, colour_code: colourCode, ...(encodeAddons(addonQty) ? { addons: encodeAddons(addonQty) } : {}) }).toString())}`}>Sign in for faster checkout</Link>
        </p>
      )}

      <div className={`co-express co-express--${expressStatus}`}>
        {expressStatus === 'loading' && (
          <div className="co-express-skeleton" aria-hidden="true"><span /></div>
        )}
        <ExpressCheckoutElement
          options={{
            paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never', amazonPay: 'never', paypal: 'never' },
            buttonHeight: 48,
          }}
          onReady={({ availablePaymentMethods }) => {
            const a = availablePaymentMethods || {};
            setExpressStatus(a.applePay || a.googlePay ? 'ready' : 'unavailable');
          }}
          onLoadError={() => setExpressStatus('unavailable')}
          onClick={handleExpressClick}
          onShippingAddressChange={handleExpressShippingChange}
          onConfirm={handleExpressConfirm}
        />
        {expressStatus === 'ready' && (
          <div className="co-express-divider"><span>or pay by card</span></div>
        )}
      </div>

      <section className="co-section">
        <h2 className="co-section-title">1. Contact</h2>
        <div className="co-field-row co-field-row--2">
          <div className="co-field">
            <label htmlFor="co-email">Email</label>
            <input
              id="co-email"
              type="email"
              autoComplete="email"
              required
              value={customer.email}
              onChange={update('email')}
            />
          </div>
          <div className="co-field">
            <label htmlFor="co-phone">Phone</label>
            <input
              id="co-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              value={customer.phone}
              onChange={update('phone')}
            />
          </div>
        </div>
      </section>

      <section className="co-section">
        <h2 className="co-section-title">2. Delivery address</h2>
        <div className="co-field">
          <label htmlFor="co-name">Full name</label>
          <input
            id="co-name"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            required
            value={customer.full_name}
            onChange={update('full_name')}
          />
        </div>
        <div className="co-field">
          <label htmlFor="co-street">Street address</label>
          <input
            id="co-street"
            type="text"
            autoComplete="street-address"
            required
            value={customer.address_line1}
            onChange={update('address_line1')}
          />
        </div>
        <div className="co-field">
          <label htmlFor="co-unit">Apartment, unit, etc. (optional)</label>
          <input
            id="co-unit"
            type="text"
            autoComplete="address-line2"
            value={customer.address_line2}
            onChange={update('address_line2')}
          />
        </div>
        <div className="co-field-row co-field-row--3">
          <div className="co-field">
            <label htmlFor="co-suburb">Suburb</label>
            <input
              id="co-suburb"
              type="text"
              autoComplete="address-level2"
              autoCapitalize="words"
              required
              value={customer.suburb}
              onChange={update('suburb')}
            />
          </div>
          <div className="co-field">
            <label htmlFor="co-state">State</label>
            <select id="co-state" autoComplete="address-level1" value={customer.state} onChange={update('state')}>
              {STATES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="co-field">
            <label htmlFor="co-postcode">Postcode</label>
            <input
              id="co-postcode"
              type="text"
              autoComplete="postal-code"
              inputMode="numeric"
              required
              maxLength={4}
              value={customer.postcode}
              onChange={update('postcode')}
            />
          </div>
        </div>
        {(() => {
          const pc = customer.postcode.trim();
          if (!/^\d{4}$/.test(pc)) return null;
          const zone = deliveryZone(pc);
          if (zone === 1) return (
            <p className="co-postcode-msg co-postcode-msg--free">
              <svg className="co-postcode-tick" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 12l5 5 9-11" /></svg>
              Free delivery to your area
            </p>
          );
          if (zone === 2) return (
            <p className="co-postcode-msg co-postcode-msg--paid">$150 delivery to your area</p>
          );
          return (
            <p className="co-postcode-msg co-postcode-msg--paid">
              We ship Australia wide - delivery to your postcode is quoted separately. <Link to="/request-quote">Request a freight quote</Link> and we will confirm before charging you.
            </p>
          );
        })()}
      </section>

      <section className="co-section">
        <h2 className="co-section-title">3. Notes <span className="co-section-optional">(optional)</span></h2>
        <textarea
          className="co-textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything we should know about delivery? Access, timing, special requests..."
        />
      </section>

      <section className="co-section">
        <h2 className="co-section-title">4. Promo code <span className="co-section-optional">(optional)</span></h2>
        {discount ? (
          <div className="co-code-applied">
            <span><strong>{discount.code}</strong> applied - ${fmtPrice(discount.discount_cents / 100)} off</span>
            <button type="button" className="co-code-remove" onClick={() => { setDiscount(null); setCodeInput(''); setCodeError(''); }}>
              Remove
            </button>
          </div>
        ) : (
          <>
            <div className="co-code-row">
              <input
                className="co-input co-code-input"
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value); setCodeError(''); }}
                placeholder="Enter code"
                autoComplete="off"
                spellCheck="false"
              />
              <button type="button" className="co-code-btn" onClick={applyCode} disabled={codeBusy || !codeInput.trim()}>
                {codeBusy ? 'Checking...' : 'Apply'}
              </button>
            </div>
            {codeError && <p className="co-code-error">{codeError}</p>}
          </>
        )}
      </section>

      <section className="co-section">
        <h2 className="co-section-title">5. Payment</h2>
        <PaymentElement options={{
          layout: 'tabs',
          // Inline wallets are off — the dedicated ExpressCheckoutElement
          // at the top of the form is the wallets surface.
          wallets: { applePay: 'never', googlePay: 'never' },
          // Tell Stripe the buyer is in AU so the card form validates a
          // 4-digit AU postcode (not a 5-digit US ZIP) and shows the
          // right field labels.
          defaultValues: {
            billingDetails: {
              email: customer.email || undefined,
              name: customer.full_name || undefined,
              phone: customer.phone || undefined,
              address: {
                country: 'AU',
                postal_code: customer.postcode || undefined,
                state: customer.state || 'VIC',
                city: customer.suburb || undefined,
                line1: customer.address_line1 || undefined,
                line2: customer.address_line2 || undefined,
              },
            },
          },
          // Hide the bottom Stripe Link "Save my info for 1-click" prompt.
          // `terms.card: never` suppresses the consent / save-info block.
          terms: { card: 'never' },
        }} />
        <p className="co-pay-note">
          Payments are processed securely by Stripe. We never see or store your card details.
        </p>
      </section>

      {error && <p className="co-error" role="alert">{error}</p>}

      <div className="co-place-order">
        {(/^\d{4}$/.test(customer.postcode.trim()) && deliveryZone(customer.postcode) === 3) ? (
          <div className="co-freight-quote">
            <p>We ship Australia wide, but delivery to your postcode is quoted separately. We will confirm the freight cost before charging you.</p>
            <Link to="/request-quote" className="co-pay-btn co-pay-btn--quote">Request a freight quote</Link>
          </div>
        ) : (
          <button type="submit" className="co-pay-btn" disabled={submitting || !stripe || !elements || !formValid}>
            {placeOrderLabel}
          </button>
        )}
      </div>
    </form>
  );
}

function CheckoutPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const productSlug = params.get('product') || '';
  const width = params.get('w') || '';
  const projection = params.get('p') || '';
  const colourName = params.get('colour') || '';
  const colourCode = params.get('colour_code') || '';
  const addonParam = params.get('addons') || '';
  const addonQty = useMemo(() => parseAddons(addonParam), [addonParam]);

  const [product, setProduct] = useState(null);
  const [productError, setProductError] = useState('');
  useEffect(() => {
    if (!productSlug) { setProductError('No product selected.'); return; }
    apiFetch(`/api/products/${encodeURIComponent(productSlug)}`)
      .then((r) => { if (!r.ok) throw new Error('Product not found.'); return r.json(); })
      .then(setProduct)
      .catch((e) => setProductError(e.message));
  }, [productSlug]);

  // Pricing — getPricing() returns { sale, original, discountPct } or null.
  // When width × projection both pass, we pass explicitSale of the matched
  // variant so the discount math anchors to the *selected* variant rather
  // than the cheapest one in the table.
  const pricing = useMemo(() => {
    if (!product) return null;
    const w = Number(width), p = Number(projection);
    const list = product.variant_prices || [];
    let explicitSale;
    if (Number.isFinite(w) && Number.isFinite(p)) {
      const match = list.find((v) => Number(v.width) === w && Number(v.projection) === p);
      if (match && match.price != null) explicitSale = match.price;
    }
    return getPricing(product, explicitSale);
  }, [product, width, projection]);

  const variantLabel = variantLabelFor(width, projection);
  // Add-ons from the link, priced for display against what this product
  // actually offers. Anything it does not offer drops out here and is refused
  // by the server, which prices the order itself.
  const addons = useMemo(() => addonLines(product?.accessory_options, addonQty), [product, addonQty]);

  const [customer, setCustomer] = useState({
    full_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    suburb: '',
    state: 'VIC',
    postcode: '',
  });
  const [notes, setNotes] = useState('');
  // The applied promotional code, as the server priced it. Held here rather
  // than in the form so both copies of the summary - the mobile card and the
  // desktop sidebar - show the same figure.
  const [discount, setDiscount] = useState(null);

  // Prefill from the signed-in user profile.
  useEffect(() => {
    if (!user) return;
    setCustomer((c) => ({
      ...c,
      full_name: c.full_name || user.full_name || '',
      email: c.email || user.email || '',
      phone: c.phone || user.phone || '',
      address_line1: c.address_line1 || user.address_line1 || '',
      address_line2: c.address_line2 || user.address_line2 || '',
      suburb: c.suburb || user.suburb || '',
      state: c.state || user.state || 'VIC',
      postcode: c.postcode || user.postcode || '',
    }));
  }, [user]);

  // Running total in cents — used to drive both the summary display and
  // the Stripe Elements amount (Apple/Google Pay button price).
  const totalCents = useMemo(() => {
    if (!pricing) return 0;
    const subtotal = Math.round(pricing.sale * 100) + Math.round(addons.total * 100);
    const validPostcode = /^\d{4}$/.test(customer.postcode.trim());
    const delivery = validPostcode ? calculateDeliveryFeeCents(customer.postcode) : 0;
    // The discount has to be in here, not only in the summary. This figure
    // labels the Place Order button and is the amount handed to Stripe
    // Elements, which is what the Apple Pay and Google Pay sheets display. A
    // wallet quoting a different number from the one about to be charged is
    // the worst place for the two to disagree.
    const discountCents = discount ? discount.discount_cents : 0;
    return Math.max(0, subtotal + (delivery || 0) - discountCents);
  }, [pricing, addons, customer.postcode, discount]);

  // Stripe Elements options — only read on mount; we use elements.update()
  // afterwards to keep the amount in sync. Mount once `totalCents > 0`.
  const elementsOptions = useMemo(() => {
    if (!totalCents) return null;
    return {
      mode: 'payment',
      amount: totalCents,
      currency: 'aud',
      paymentMethodTypes: ['card'],
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: 'var(--asg-cta)',
          fontFamily: 'Outfit, -apple-system, BlinkMacSystemFont, sans-serif',
          borderRadius: '8px',
        },
      },
    };
    // We intentionally do NOT depend on totalCents here — we mount once,
    // then update via elements.update(). Re-creating options would re-mount
    // the Element and wipe the user's entered card details.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(totalCents)]);

  // Rendered in every branch below, including the loading and error states.
  // The checkout is transactional and parameterised, so it is noindex; the
  // tags still need to be there so crawlers (and the browser tab) get a
  // title, a description and the noindex directive whichever state they hit.
  const seo = (
    <SEO
      title={product ? `Checkout - ${product.name}` : 'Checkout'}
      description="Secure checkout for your ASG Shading order - contact details, delivery address and card payment, processed by Stripe."
      canonical="/checkout"
      noindex
    />
  );

  // While pre-rendering there is never a product, and the captured HTML
  // must be what the client renders first (the loading state) rather than
  // the "no product" message that only appears once the effect has run.
  if (!product && (isPrerendering || !productError)) {
    return (
      <div className="co-page">
        {seo}
        <div className="co-inner co-empty"><h1>Checkout</h1><p>Loading checkout…</p></div>
      </div>
    );
  }
  if (productError) {
    return (
      <div className="co-page">
        {seo}
        <div className="co-inner co-empty">
          <h1>Checkout</h1>
          <p>{productError}</p>
          <button onClick={() => navigate(-1)} className="co-back-btn">← Back</button>
        </div>
      </div>
    );
  }
  if (!STRIPE_PK) {
    return (
      <div className="co-page">
        {seo}
        <div className="co-inner co-empty">
          <h1>Checkout temporarily unavailable</h1>
          <p>Payments aren't configured yet. Please <Link to="/contact">contact us</Link> to place this order.</p>
        </div>
      </div>
    );
  }

  const summaryProps = {
    product,
    variantLabel,
    colourName,
    pricing,
    addons,
    postcodeStr: customer.postcode,
    discount,
  };

  return (
    <div className="co-page">
      {seo}

      {/* Mobile-only full summary card — sits above the form on small screens.
          The desktop sidebar handles the same job at ≥901px. */}
      <div className="co-mobile-summary">
        <OrderSummary {...summaryProps} />
      </div>

      <div className="co-inner">
        <div className="co-grid">
          <div className="co-left">
            <h1 className="co-title">Checkout</h1>
            {elementsOptions ? (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <CheckoutForm
                  product={product}
                  pricing={pricing}
                  variantLabel={variantLabel}
                  colourName={colourName}
                  customer={customer}
                  setCustomer={setCustomer}
                  notes={notes}
                  setNotes={setNotes}
                  productSlug={productSlug}
                  width={width}
                  projection={projection}
                  colourCode={colourCode}
                  addons={addons}
                  addonQty={addonQty}
                  user={user}
                  totalCents={totalCents}
                  navigate={navigate}
                  discount={discount}
                  setDiscount={setDiscount}
                />
              </Elements>
            ) : (
              <p>Preparing checkout…</p>
            )}
          </div>

          <aside className="co-right">
            <OrderSummary {...summaryProps} />
          </aside>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
